import React, { useState, useRef, useCallback } from 'react'
import { Box, LoadingOverlay } from '@mantine/core'
import { Rectangle } from './Hardware/types'

const PhotoComparisonOverlay: React.FC<{
  imageUrl?: string
  onSquareDrawn?: (square: Rectangle) => void
  onSquareMoved?: (square: Rectangle) => void
  onSquareResized?: (square: Rectangle) => void
  onSquareDeleted?: (square: Rectangle) => void
  className?: string
  selectedSquare: Rectangle | null
  setSelectedSquare: (square: Rectangle | null) => void
  isLoading?: boolean
  square: Rectangle | null
  setSquare: (square: Rectangle | null) => void
  onImageLoad?: (imageElement: HTMLImageElement) => void
}> = ({
  imageUrl,
  onSquareDrawn,
  onSquareMoved,
  onSquareResized,
  onSquareDeleted,
  selectedSquare,
  setSelectedSquare,
  className,
  isLoading,
  square,
  setSquare,
  onImageLoad,
}) => {
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentSquare, setCurrentSquare] = useState<Partial<Rectangle> | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeHandle, setResizeHandle] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null>(
    null
  )
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const rafIdRef = useRef<number | null>(null)
  const containerSizeRef = useRef({ width: 0, height: 0 })

  const getMousePosition = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 }
    const rect = containerRef.current.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }, [])

  const getFixedPos = useCallback((handle: string, sq: Rectangle) => {
    switch (handle) {
      case 'top-left':
        return { x: sq.x + sq.width, y: sq.y + sq.height }
      case 'top-right':
        return { x: sq.x, y: sq.y + sq.height }
      case 'bottom-left':
        return { x: sq.x + sq.width, y: sq.y }
      case 'bottom-right':
        return { x: sq.x, y: sq.y }
      default:
        return { x: 0, y: 0 }
    }
  }, [])

  const scheduleUpdate = useCallback((updateFn: () => void) => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
    }
    rafIdRef.current = requestAnimationFrame(() => {
      updateFn()
      rafIdRef.current = null
    })
  }, [])

  const clampRectangle = useCallback((rect: Rectangle): Rectangle => {
    const { width: maxWidth, height: maxHeight } = containerSizeRef.current
    const ratio = 16 / 9

    let clampedRect = { ...rect }

    // Ensure minimum size with aspect ratio
    if (clampedRect.width < 10) {
      clampedRect.width = 10
      clampedRect.height = 10 / ratio
    }
    if (clampedRect.height < 10) {
      clampedRect.height = 10
      clampedRect.width = 10 * ratio
    }

    // Clamp position - keep rectangle within bounds
    clampedRect.x = Math.max(0, Math.min(clampedRect.x, maxWidth - clampedRect.width))
    clampedRect.y = Math.max(0, Math.min(clampedRect.y, maxHeight - clampedRect.height))

    // If rectangle still extends beyond bounds, shrink it maintaining aspect ratio
    if (clampedRect.x + clampedRect.width > maxWidth) {
      const maxAllowedWidth = maxWidth - clampedRect.x
      clampedRect.width = maxAllowedWidth
      clampedRect.height = clampedRect.width / ratio
    }

    if (clampedRect.y + clampedRect.height > maxHeight) {
      const maxAllowedHeight = maxHeight - clampedRect.y
      clampedRect.height = maxAllowedHeight
      clampedRect.width = clampedRect.height * ratio
      // Re-check width after height adjustment
      if (clampedRect.x + clampedRect.width > maxWidth) {
        clampedRect.width = maxWidth - clampedRect.x
        clampedRect.height = clampedRect.width / ratio
      }
    }

    // Final position adjustment to ensure it's within bounds
    clampedRect.x = Math.max(0, Math.min(clampedRect.x, maxWidth - clampedRect.width))
    clampedRect.y = Math.max(0, Math.min(clampedRect.y, maxHeight - clampedRect.height))

    return clampedRect
  }, [])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return // Only left click

      const pos = getMousePosition(e)

      // Check for resize handle first (if square is selected)
      let selectedHandle: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null = null
      if (square && selectedSquare?.id === square.id) {
        const tl = { x: square.x, y: square.y }
        const tr = { x: square.x + square.width, y: square.y }
        const bl = { x: square.x, y: square.y + square.height }
        const br = { x: square.x + square.width, y: square.y + square.height }
        const tolerance = 6

        if (Math.hypot(pos.x - tl.x, pos.y - tl.y) <= tolerance) {
          selectedHandle = 'top-left'
        } else if (Math.hypot(pos.x - tr.x, pos.y - tr.y) <= tolerance) {
          selectedHandle = 'top-right'
        } else if (Math.hypot(pos.x - bl.x, pos.y - bl.y) <= tolerance) {
          selectedHandle = 'bottom-left'
        } else if (Math.hypot(pos.x - br.x, pos.y - br.y) <= tolerance) {
          selectedHandle = 'bottom-right'
        }
      }

      if (selectedHandle) {
        setResizeHandle(selectedHandle)
        setIsResizing(true)
        return
      }

      // Check if clicking on existing square (for dragging)
      const clickedSquare =
        square &&
        pos.x >= square.x &&
        pos.x <= square.x + square.width &&
        pos.y >= square.y &&
        pos.y <= square.y + square.height

      if (clickedSquare) {
        setSelectedSquare(square)
        setIsDragging(true)
        setDragStart({
          x: pos.x - square.x,
          y: pos.y - square.y,
        })
        return
      }

      if (!isDrawing) {
        // First click - start drawing (clear existing square first)
        setSquare(null) // Remove any existing square
        setIsDrawing(true)
        setSelectedSquare(null)
        setCurrentSquare({
          id: `square-${Date.now()}`,
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
        })
      } else {
        // Second click - finish drawing
        if (currentSquare) {
          const endX = currentSquare.x! + (currentSquare.width || 0)
          const endY = currentSquare.y! + (currentSquare.height || 0)
          const newSquare: Rectangle = {
            id: currentSquare.id!,
            x: Math.min(currentSquare.x!, endX),
            y: Math.min(currentSquare.y!, endY),
            width: Math.abs(endX - currentSquare.x!),
            height: Math.abs(endY - currentSquare.y!),
          }

          if (newSquare.width > 10 && newSquare.height > 10) {
            setSquare(newSquare) // Store displayed coordinates
            setSelectedSquare(newSquare) // Store displayed coordinates
            onSquareDrawn?.(newSquare) // Pass displayed coordinates
          }

          setIsDrawing(false)
          setCurrentSquare(null)
        }
      }
    },
    [square, selectedSquare, getMousePosition, isDrawing, currentSquare, onSquareDrawn, setSquare, setSelectedSquare]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const pos = getMousePosition(e)

      if (isDragging && selectedSquare && square) {
        const newSquare = clampRectangle({
          ...square,
          x: pos.x - dragStart.x,
          y: pos.y - dragStart.y,
        })
        scheduleUpdate(() => {
          setSquare(newSquare)
          setSelectedSquare(newSquare) // Also update selected square
        })
      } else if (isResizing && resizeHandle && square) {
        const fixed = getFixedPos(resizeHandle, square)
        const intendedX = pos.x
        const intendedY = pos.y
        let dx = intendedX - fixed.x
        let dy = intendedY - fixed.y
        const absDx = Math.abs(dx)
        const absDy = Math.abs(dy)

        let newDx = dx
        let newDy = dy

        const ratio = 16 / 9

        if (absDx > 0 && absDy > 0) {
          if (absDx / absDy > ratio) {
            // Too wide: constrain width based on height
            newDx = ratio * absDy * Math.sign(dx)
            newDy = dy
          } else {
            // Too tall: constrain height based on width
            newDy = (absDx / ratio) * Math.sign(dy)
            newDx = dx
          }
        }

        // Apply height constraint: no higher than 300px
        const maxHeight = 300
        if (Math.abs(newDy) > maxHeight) {
          const cappedAbsHeight = maxHeight
          newDy = cappedAbsHeight * Math.sign(newDy)
          newDx = ratio * cappedAbsHeight * Math.sign(dx)
        }

        // Clamp to prevent flipping (ensure width/height >= 0)
        const isLeftHandle = resizeHandle === 'top-left' || resizeHandle === 'bottom-left'
        const isTopHandle = resizeHandle === 'top-left' || resizeHandle === 'top-right'

        let newDraggedX = fixed.x + newDx
        let newDraggedY = fixed.y + newDy

        if (isLeftHandle) {
          newDraggedX = Math.min(newDraggedX, fixed.x)
        } else {
          newDraggedX = Math.max(newDraggedX, fixed.x)
        }

        if (isTopHandle) {
          newDraggedY = Math.min(newDraggedY, fixed.y)
        } else {
          newDraggedY = Math.max(newDraggedY, fixed.y)
        }

        // Compute new square based on handle
        let newSquare: Rectangle = { ...square }
        switch (resizeHandle) {
          case 'top-left':
            newSquare.x = newDraggedX
            newSquare.y = newDraggedY
            newSquare.width = fixed.x - newDraggedX
            newSquare.height = fixed.y - newDraggedY
            break
          case 'top-right':
            newSquare.width = newDraggedX - square.x
            newSquare.y = newDraggedY
            newSquare.height = fixed.y - newDraggedY
            break
          case 'bottom-left':
            newSquare.x = newDraggedX
            newSquare.height = newDraggedY - square.y
            newSquare.width = fixed.x - newDraggedX
            break
          case 'bottom-right':
            newSquare.width = newDraggedX - square.x
            newSquare.height = newDraggedY - square.y
            break
        }

        // Ensure minimum size (snap to min with ratio)
        if (newSquare.width < 10 || newSquare.height < 10) {
          const minHeight = 10
          const minWidth = ratio * minHeight
          let minDraggedX: number
          let minDraggedY: number
          switch (resizeHandle) {
            case 'top-left':
              minDraggedX = fixed.x - minWidth
              minDraggedY = fixed.y - minHeight
              break
            case 'top-right':
              minDraggedX = square.x + minWidth
              minDraggedY = fixed.y - minHeight
              break
            case 'bottom-left':
              minDraggedX = fixed.x - minWidth
              minDraggedY = square.y + minHeight
              break
            case 'bottom-right':
              minDraggedX = square.x + minWidth
              minDraggedY = square.y + minHeight
              break
          }
          newSquare.width = minWidth
          newSquare.height = minHeight
          if (resizeHandle === 'top-left' || resizeHandle === 'bottom-left') {
            newSquare.x = minDraggedX
          }
          if (resizeHandle === 'top-left' || resizeHandle === 'top-right') {
            newSquare.y = minDraggedY
          }
        }

        // Ensure aspect ratio is maintained before clamping
        const currentRatio = newSquare.width / newSquare.height
        if (Math.abs(currentRatio - ratio) > 0.01) {
          // Adjust to maintain aspect ratio - prefer keeping width
          newSquare.height = newSquare.width / ratio
          // If height adjustment would cause issues, adjust width instead
          if (newSquare.height < 10) {
            newSquare.height = 10
            newSquare.width = newSquare.height * ratio
          }
        }

        const clampedSquare = clampRectangle(newSquare)
        scheduleUpdate(() => {
          setSquare(clampedSquare)
          setSelectedSquare(clampedSquare)
        })
      } else if (isDrawing && currentSquare) {
        // Update the preview square as mouse moves
        scheduleUpdate(() => {
          setCurrentSquare(prev => {
            if (!prev || prev.x === undefined || prev.y === undefined) return null

            const startX = prev.x
            const startY = prev.y
            const dx = pos.x - startX
            const dy = pos.y - startY
            const absDx = Math.abs(dx)
            const absDy = Math.abs(dy)

            let newWidth = dx
            let newHeight = dy

            const ratio = 16 / 9

            if (absDx > 0 && absDy > 0) {
              if (absDx / absDy > ratio) {
                // Too wide: constrain width based on height
                newWidth = ratio * absDy * Math.sign(dx)
                newHeight = dy
              } else {
                // Too tall: constrain height based on width
                newHeight = (absDx / ratio) * Math.sign(dy)
                newWidth = dx
              }
            }

            // Apply height constraint: no higher than 300px
            const maxHeight = 300
            if (Math.abs(newHeight) > maxHeight) {
              const cappedAbsHeight = maxHeight
              newHeight = cappedAbsHeight * Math.sign(newHeight)
              newWidth = ratio * cappedAbsHeight * Math.sign(dx)
            }

            return {
              ...prev,
              width: newWidth,
              height: newHeight,
            }
          })
        })
      }
    },
    [
      isDragging,
      selectedSquare,
      square,
      dragStart,
      getMousePosition,
      isResizing,
      resizeHandle,
      getFixedPos,
      setSquare,
      setSelectedSquare,
      isDrawing,
      currentSquare,
      clampRectangle,
      scheduleUpdate,
    ]
  )

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false)
      if (square) onSquareMoved?.(square)
    }
    if (isResizing) {
      setIsResizing(false)
      setResizeHandle(null)
      if (square) onSquareResized?.(square)
    }
  }, [isDragging, isResizing, square, onSquareMoved, onSquareResized])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Delete' && selectedSquare !== null) {
        setSquare(null) // Clear the square
        onSquareDeleted?.(selectedSquare)
        setSelectedSquare(null)
      }
    },
    [selectedSquare, onSquareDeleted, setSquare, setSelectedSquare]
  )

  // Cleanup pending animation frames on unmount
  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [])

  return (
    <Box
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        display: 'block',
        cursor: isDrawing ? 'crosshair' : 'default',
        height: '100vh',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt="Photo for comparison"
          style={{ width: '100%', height: '100%', display: 'block' }}
          onLoad={e => {
            const img = e.currentTarget
            const rect = containerRef.current?.getBoundingClientRect()
            if (rect) {
              containerSizeRef.current = { width: rect.width, height: rect.height }
            }
            onImageLoad?.(img)
          }}
        />
      )}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: isLoading ? 'block' : 'none',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          visibility: isLoading ? 'visible' : 'hidden',
        }}
      >
        <LoadingOverlay visible={isLoading} />
      </Box>

      <svg
        ref={svgRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {/* Render the single square */}
        {square && square.width > 0 && square.height > 0 && (
          <g key={square.id}>
            <rect
              x={Math.max(0, square.x)}
              y={Math.max(0, square.y)}
              width={Math.max(0, square.width)}
              height={Math.max(0, square.height)}
              fill={selectedSquare?.id === square.id ? 'rgba(51, 154, 240, 0.3)' : 'rgba(116, 192, 252, 0.3)'}
              stroke={selectedSquare?.id === square.id ? '#1c7ed6' : '#339af0'}
              strokeWidth="2"
              style={{
                pointerEvents: 'all',
                cursor: selectedSquare?.id === square.id ? 'move' : 'default',
              }}
            />
            {selectedSquare?.id === square.id && (
              <>
                {/* Invisible larger circles for better hit detection */}
                <circle
                  cx={square.x}
                  cy={square.y}
                  r="20"
                  fill="transparent"
                  style={{ pointerEvents: 'all', cursor: 'nw-resize' }}
                />
                <circle
                  cx={square.x + square.width}
                  cy={square.y}
                  r="20"
                  fill="transparent"
                  style={{ pointerEvents: 'all', cursor: 'ne-resize' }}
                />
                <circle
                  cx={square.x}
                  cy={square.y + square.height}
                  r="20"
                  fill="transparent"
                  style={{ pointerEvents: 'all', cursor: 'sw-resize' }}
                />
                <circle
                  cx={square.x + square.width}
                  cy={square.y + square.height}
                  r="20"
                  fill="transparent"
                  style={{ pointerEvents: 'all', cursor: 'se-resize' }}
                />

                {/* Visible smaller circles */}
                <circle cx={square.x} cy={square.y} r="10" fill="#1c7ed6" style={{ pointerEvents: 'none' }} />
                <circle
                  cx={square.x + square.width}
                  cy={square.y}
                  r="10"
                  fill="#1c7ed6"
                  style={{ pointerEvents: 'none' }}
                />
                <circle
                  cx={square.x}
                  cy={square.y + square.height}
                  r="10"
                  fill="#1c7ed6"
                  style={{ pointerEvents: 'none' }}
                />
                <circle
                  cx={square.x + square.width}
                  cy={square.y + square.height}
                  r="10"
                  fill="#1c7ed6"
                  style={{ pointerEvents: 'none' }}
                />
              </>
            )}
          </g>
        )}

        {/* Render current drawing square */}
        {currentSquare && (
          <rect
            x={currentSquare.width! < 0 ? currentSquare.x! + currentSquare.width! : currentSquare.x!}
            y={currentSquare.height! < 0 ? currentSquare.y! + currentSquare.height! : currentSquare.y!}
            width={Math.abs(currentSquare.width!)}
            height={Math.abs(currentSquare.height!)}
            fill="rgba(116, 192, 252, 0.3)"
            stroke="#339af0"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
        )}
      </svg>
    </Box>
  )
}

export default PhotoComparisonOverlay
